import React, { useEffect, useMemo, useState } from 'react';
import StageShell from './common/StageShell';
import { getProject, setStage } from '../../services/sdlcProject.service';
import { synthesizeArchitectureOptions } from './sdlcSynthesizers';

/**
 * SDLC – Architecture stage.
 *
 * Synthesises 3 candidate architectures from the requirements report and
 * lets the user pick one. The selection is persisted onto the SDLC project
 * so the Code, Tests and Deployment stages can branch on it.
 */
function ArchitectureStage() {
  const [project, setProject] = useState(getProject());
  const requirementsReport = project?.requirements?.snapshot || null;

  const generated = useMemo(
    () => synthesizeArchitectureOptions(requirementsReport),
    [requirementsReport],
  );

  // Hydrate the stage with synthesized options if empty (or the report changed).
  useEffect(() => {
    const current = project?.architecture;
    const sameOptions = current?.options?.length === generated.length &&
      current?.options?.every((o, i) => o.id === generated[i].id);
    if (!sameOptions) {
      const next = setStage('architecture', {
        options: generated,
        selectedId: current?.selectedId && generated.some((g) => g.id === current.selectedId)
          ? current.selectedId
          : null,
      });
      setProject(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generated]);

  const selectedId = project?.architecture?.selectedId || null;
  const options = project?.architecture?.options || generated;

  const handleSelect = (id) => {
    const next = setStage('architecture', {
      ...(project.architecture || { options: generated }),
      selectedId: id,
    });
    setProject(next);
  };

  const handleRegenerate = () => {
    const fresh = synthesizeArchitectureOptions(requirementsReport);
    const next = setStage('architecture', { options: fresh, selectedId: null });
    setProject(next);
  };

  const inputs = [
    { label: 'Idea', value: project?.idea?.title || '' },
    {
      label: 'Requirements',
      value: project?.requirements?.snapshot
        ? (project.requirements.snapshot.title || project.requirements.reportId)
        : '',
    },
  ];

  return (
    <StageShell
      stageId="architecture"
      description="Pick an architecture style. The choice flows into Code, Tests and Deployment."
      inputs={inputs}
      canContinue={Boolean(selectedId)}
      actions={<button className="stage-btn" onClick={handleRegenerate}>Regenerate options</button>}
    >
      {!requirementsReport && (
        <div className="stage-empty">
          <div className="icon">📋</div>
          No requirements report bound. Pick or generate one in the Requirements stage first.
        </div>
      )}

      <div className="stage-options">
        {options.map((opt) => (
          <div
            key={opt.id}
            className={`stage-option ${selectedId === opt.id ? 'selected' : ''}`}
            onClick={() => handleSelect(opt.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelect(opt.id); }}
          >
            <h4>{opt.name}</h4>
            <div className="meta">{opt.summary}</div>
            <ul>
              {opt.components.slice(0, 5).map((c, i) => (<li key={i}>{c}</li>))}
              {opt.components.length > 5 && <li>…and {opt.components.length - 5} more</li>}
            </ul>
            <div style={{ marginTop: 8, fontSize: '0.82rem' }}>
              <strong>Best for:</strong> {opt.bestFor}
            </div>
          </div>
        ))}
      </div>

      {selectedId && (
        <div style={{ marginTop: 18, padding: 12, background: '#ecfdf5', borderRadius: 8, color: '#065f46', fontSize: '0.9rem' }}>
          ✅ Selected: <strong>{options.find((o) => o.id === selectedId)?.name}</strong>
        </div>
      )}
    </StageShell>
  );
}

export default ArchitectureStage;
